import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';
import { AuthTokens } from '../types';
import { authService } from '../services/authService';
import { usersService } from '../services/usersService';
import { isTokenExpired, getTokenExpiration, isTokenExpiringSoon, parseJWT } from '../utils/jwt';

interface UserProfile {
  id: string;
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  roleId?: string;
  roleName?: string;
  permissions?: string[]; // Permissions from JWT token
}

interface AuthContextType {
  isAuthenticated: boolean;
  tokens: AuthTokens | null;
  user: UserProfile | null;
  login: (tokens: AuthTokens) => Promise<void>;
  logout: () => void;
  refreshTokens: () => Promise<boolean>;
  isRefreshing: boolean;
  isInitializing: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const hasInitializedRef = useRef<boolean>(false);
  const refreshTokensRef = useRef<((tokensToUse?: AuthTokens | null) => Promise<boolean>) | null>(null);

  // Extract user information from JWT token
  const extractUserFromToken = useCallback((accessToken: string): UserProfile | null => {
    try {
      const payload = parseJWT(accessToken);
      if (!payload) return null;

      // Extract user information from JWT payload
      // Common JWT claims: sub (subject/user ID), email, name, given_name, family_name
      const userId = payload.sub || payload.userId || payload.id || '';
      const email = payload.email || payload.email_address || '';
      const firstName = payload.given_name || payload.firstName || payload.first_name || '';
      const lastName = payload.family_name || payload.lastName || payload.last_name || '';
      const name = payload.name || payload.fullName || payload.displayName || 
                   (firstName && lastName ? `${firstName} ${lastName}`.trim() : '') ||
                   email.split('@')[0] || 'User';

      // Extract role information from JWT payload
      // Try multiple possible claim names for role ID
      const roleId = payload.roleId || payload.role_id || payload.role || payload.roles?.[0] || '';
      const roleName = payload.roleName || payload.role_name || '';

      // Extract permissions from JWT payload
      // Try multiple possible claim names for permissions
      const permissions = payload.permissions || payload.permission || payload.perms || [];
      // Ensure permissions is an array
      const permissionsArray = Array.isArray(permissions) ? permissions : [];

      // Log JWT payload for debugging
      console.log('JWT Token Payload:', {
        fullPayload: payload,
        roleId,
        roleName,
        permissions: permissionsArray,
        permissionsCount: permissionsArray.length,
        allKeys: Object.keys(payload)
      });

      return {
        id: userId,
        email,
        name,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        roleId: roleId || undefined,
        roleName: roleName || undefined,
        permissions: permissionsArray.length > 0 ? permissionsArray : undefined,
      };
    } catch (error) {
      console.error('Error extracting user from token:', error);
      return null;
    }
  }, []);

  // Fetch user role from API if not in JWT token
  const fetchUserRole = useCallback(async (userId: string, tokens: AuthTokens): Promise<{ roleId?: string; roleName?: string }> => {
    try {
      // Initialize usersService with tokens
      usersService.initialize(tokens, (newTokens) => {
        // Token refresh callback - update stored tokens
        const enhancedTokens: AuthTokens = {
          ...newTokens,
          accessTokenExpiry: newTokens.accessTokenExpiry || getTokenExpiration(newTokens.accessToken) || undefined,
          refreshTokenExpiry: newTokens.refreshTokenExpiry || getTokenExpiration(newTokens.refreshToken) || undefined,
        };
        setTokens(enhancedTokens);
        localStorage.setItem('authTokens', JSON.stringify(enhancedTokens));
      });

      // Fetch user data from API
      const response = await usersService.getUserById(userId);
      if (response?.succeeded && response?.data) {
        const userData = response.data;
        // Extract roleId and roleName from API response
        // API may return roleId directly or in roleIds array
        const roleId = userData.roleId || userData.roleIds?.[0] || '';
        // API may return roleName directly or we need to fetch role name by ID
        const roleName = userData.roleName || '';
        
        console.log('Fetched user role from API:', { 
          roleId, 
          roleName, 
          userData,
          roleIdField: userData.roleId,
          roleIdsArray: userData.roleIds,
          allKeys: Object.keys(userData)
        });
        return { roleId, roleName };
      }
    } catch (error) {
      console.error('Error fetching user role from API:', error);
    }
    return {};
  }, []);

  // Enhanced token storage with expiration timestamps
  const storeTokens = useCallback(async (newTokens: AuthTokens) => {
    // Calculate expiration timestamps if not already present
    const enhancedTokens: AuthTokens = {
      ...newTokens,
      accessTokenExpiry: newTokens.accessTokenExpiry || getTokenExpiration(newTokens.accessToken) || undefined,
      refreshTokenExpiry: newTokens.refreshTokenExpiry || getTokenExpiration(newTokens.refreshToken) || undefined,
    };

    setTokens(enhancedTokens);
    setIsAuthenticated(true);
    
    // Extract user information from the access token
    let userProfile = extractUserFromToken(newTokens.accessToken);
    
    // If role is not in JWT token, fetch it from API
    if (userProfile && (!userProfile.roleId && !userProfile.roleName) && userProfile.id) {
      const roleInfo = await fetchUserRole(userProfile.id, enhancedTokens);
      if (roleInfo.roleId || roleInfo.roleName) {
        userProfile = {
          ...userProfile,
          roleId: roleInfo.roleId || userProfile.roleId,
          roleName: roleInfo.roleName || userProfile.roleName,
        };
      }
    }
    
    setUser(userProfile);
    localStorage.setItem('authTokens', JSON.stringify(enhancedTokens));
  }, [extractUserFromToken, fetchUserRole]);

  // Check if tokens are valid (not expired)
  const areTokensValid = useCallback((tokens: AuthTokens | null): boolean => {
    if (!tokens || !tokens.accessToken) {
      return false;
    }

    // Check if access token is expired
    if (isTokenExpired(tokens.accessToken)) {
      return false;
    }

    return true;
  }, []);

  // Logout function - defined early so it can be used in refreshTokens
  const logout = useCallback(() => {
    setTokens(null);
    setUser(null);
    setIsAuthenticated(false);
    setIsRefreshing(false);
    localStorage.removeItem('authTokens');
  }, []);

  // Refresh tokens using refresh token
  // Can optionally accept tokens parameter for initial load scenarios
  const refreshTokens = useCallback(async (tokensToUse?: AuthTokens | null): Promise<boolean> => {
    const tokensToRefresh = tokensToUse ?? tokens;
    
    if (!tokensToRefresh?.refreshToken || isRefreshing) {
      return false;
    }

    // Check if refresh token is expired
    if (isTokenExpired(tokensToRefresh.refreshToken)) {
      console.log('Refresh token expired, logging out');
      logout();
      return false;
    }

    setIsRefreshing(true);

    try {
      const response = await authService.refreshToken({
        refreshToken: tokensToRefresh.refreshToken,
      });

      if (response.succeeded && response.data) {
        const newTokens: AuthTokens = {
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
        };
        
        storeTokens(newTokens);
        console.log('Tokens refreshed successfully');
        return true;
      } else {
        console.error('Token refresh failed:', response.errorDescription);
        logout();
        return false;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      logout();
      return false;
    } finally {
      setIsRefreshing(false);
    }
  }, [tokens, isRefreshing, storeTokens, logout]);

  // Keep refreshTokensRef updated whenever refreshTokens changes
  useEffect(() => {
    refreshTokensRef.current = refreshTokens;
  }, [refreshTokens]);

  // Check for existing tokens on app load and validate them
  // IMPORTANT: This must only run ONCE on mount, not when refreshTokens changes
  useEffect(() => {
    // Prevent re-initialization if already done
    if (hasInitializedRef.current) {
      return;
    }
    
    hasInitializedRef.current = true;
    
    const initializeAuth = async () => {
      setIsInitializing(true);
      const storedTokens = localStorage.getItem('authTokens');
      if (storedTokens) {
        try {
          const parsedTokens: AuthTokens = JSON.parse(storedTokens);
          
          // Validate tokens
          if (areTokensValid(parsedTokens)) {
            setTokens(parsedTokens);
            setIsAuthenticated(true);
            
            // Extract user information from stored token
            const userProfile = extractUserFromToken(parsedTokens.accessToken);
            setUser(userProfile);
          } else {
            // Access token is expired, but check if we can refresh
            console.log('Access token expired, attempting to refresh...');
            
            // Check if refresh token exists and is still valid
            if (parsedTokens.refreshToken && !isTokenExpired(parsedTokens.refreshToken)) {
              // Attempt to refresh tokens
              // We need to use the current refreshTokens function, but we can't include it in deps
              // So we'll call it directly - it will use the latest tokens from closure
              const refreshSuccess = await refreshTokens(parsedTokens);
              
              if (!refreshSuccess) {
                console.log('Token refresh failed, clearing storage');
                localStorage.removeItem('authTokens');
              }
            } else {
              // Refresh token is also expired or missing, clear everything
              console.log('Refresh token expired or missing, clearing storage');
              localStorage.removeItem('authTokens');
            }
          }
        } catch (error) {
          console.error('Error parsing stored tokens:', error);
          localStorage.removeItem('authTokens');
        }
      }
      setIsInitializing(false);
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array - only run once on mount

  // Set up automatic token refresh
  useEffect(() => {
    if (!tokens || !isAuthenticated) {
      return;
    }

    // Check if access token is expiring soon (within 5 minutes)
    // Only check once when tokens change, not on every render
    const shouldRefresh = isTokenExpiringSoon(tokens.accessToken, 5);
    if (shouldRefresh && !isRefreshing) {
      console.log('Access token expiring soon, attempting refresh');
      refreshTokens();
    }

    // Set up interval to check token expiration every minute
    // Use refreshTokensRef to avoid including refreshTokens in dependencies
    // This prevents the loop: refreshTokens -> updates tokens -> recreates refreshTokens -> retriggers effect
    let intervalId: NodeJS.Timeout;
    
    const checkAndRefresh = () => {
      // Use the latest tokens and refreshTokens from refs
      // The interval closure will capture the current values when the interval is created
      // When tokens change, the effect re-runs and creates a new interval with fresh values
      if (tokens && isAuthenticated && !isRefreshing && refreshTokensRef.current) {
        if (isTokenExpired(tokens.accessToken)) {
          console.log('Access token expired, attempting refresh');
          refreshTokensRef.current();
        } else if (isTokenExpiringSoon(tokens.accessToken, 5)) {
          console.log('Access token expiring soon, attempting refresh');
          refreshTokensRef.current();
        }
      }
    };

    intervalId = setInterval(checkAndRefresh, 60000); // Check every minute

    return () => clearInterval(intervalId);
  }, [tokens?.accessToken, isAuthenticated, isRefreshing]); // Removed refreshTokens - using ref instead

  const login = async (newTokens: AuthTokens) => {
    await storeTokens(newTokens);
    // Mark initialization as complete since user is explicitly logging in
    setIsInitializing(false);
    hasInitializedRef.current = true;
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      tokens, 
      user,
      login, 
      logout, 
      refreshTokens, 
      isRefreshing,
      isInitializing
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 